<template>
  <v-row>
    <!-- SIM Technical Info -->
    <v-col cols="12" md="6">
      <h4 class="mb-2">
        <v-icon class="mr-1" size="small">mdi-sim</v-icon>
        Informatii tehnice SIM
      </h4>
      <div v-if="item.sim_id" class="mb-1">
        <strong>SIM ID:</strong>
        <span class="clickable-link ml-1" @click="navigateToSim(item.sim_id)">
          #{{ item.sim_id }}
        </span>
        <v-chip
          v-if="item.sim_status_name"
          size="x-small"
          :color="getSimStatusColor(item.sim_status_name)"
          class="ml-2"
        >
          {{ item.sim_status_name }}
        </v-chip>
      </div>
      <div v-if="item.imsi_number" class="mb-1">
        <strong>IMSI:</strong>
        <span class="clickable-link ml-1" @click="navigateToImsis(item.imsi_number)">
          {{ item.imsi_number }}
        </span>
      </div>
      <div v-if="item.serial_number" class="mb-1">
        <strong>Serial:</strong>
        <span class="clickable-link ml-1" @click="navigateToImsis(item.serial_number)">
          {{ item.serial_number }}
        </span>
      </div>
      <div v-if="item.msisdn_number" class="mb-1">
        <strong>MSISDN:</strong> {{ item.msisdn_number }}
      </div>
      <div v-if="item.sim_status_name" class="mb-1">
        <strong>Status retea:</strong>
        <v-chip
          size="x-small"
          :color="getSimStatusColor(item.sim_status_name)"
          class="ml-1"
        >
          {{ item.sim_status_name }}
        </v-chip>
      </div>
      <div v-if="item.subscriber_id" class="mb-1">
        <strong>subscriber_id:</strong> {{ item.subscriber_id }}
      </div>
      <div v-if="item.subscriberID" class="mb-1">
        <strong>subscriberID:</strong> {{ item.subscriberID }}
      </div>
      <div v-if="item.sim_created_at" class="mb-1">
        <strong>SIM creat:</strong> {{ formatDate(item.sim_created_at) }}
      </div>
      <div v-if="item.sim_updated_at" class="mb-1">
        <strong>SIM actualizat:</strong> {{ formatDate(item.sim_updated_at) }}
      </div>
      <div v-if="!item.sim_id && !item.imsi_number && !item.serial_number && !item.msisdn_number">
        <em class="text-grey">Nu exista informatii tehnice SIM</em>
      </div>
      <!-- Update Info Button -->
      <div v-if="item.sim_id && showUpdateButton" class="mt-3">
        <v-btn
          color="primary"
          variant="elevated"
          size="small"
          :loading="updating"
          @click="$emit('update-sim-info', item)"
        >
          <v-icon class="mr-1">mdi-refresh</v-icon>
          Update mobile network info
        </v-btn>
      </div>
    </v-col>

    <!-- Client Service Details -->
    <v-col cols="12" md="6">
      <h4 class="mb-2">Detalii Client Service</h4>
      <div>
        <strong>Client:</strong>
        <span class="clickable-link ml-1" @click="navigateToClient(item.client_id)">
          {{ item.client_name }}
        </span>
      </div>
      <div><strong>Descriere:</strong> {{ item.description || '-' }}</div>
      <div><strong>Nume public:</strong> {{ item.public_name || '-' }}</div>
      <div><strong>Autorenew:</strong> {{ item.autorenew ? 'Da' : 'Nu' }}</div>
      <div><strong>Data start:</strong> {{ formatDate(item.start_date) }}</div>
      <div><strong>Data expirare:</strong> {{ formatDate(item.expiration_date) }}</div>
      <div><strong>Data creare:</strong> {{ formatDate(item.created_at) }}</div>
    </v-col>
  </v-row>

  <!-- Action Buttons -->
  <v-row class="mt-2">
    <v-col cols="12" class="d-flex ga-2 flex-wrap">
      <v-btn
        v-if="showCommentsButton"
        color="info"
        variant="outlined"
        size="small"
        :to="`/comments?comment_type_id=5&element_id=${item.id}`"
      >
        <v-icon class="mr-1">mdi-comment-text-multiple</v-icon>
        {{ commentsButtonText }}
      </v-btn>
      <v-btn
        v-if="item.sim_id && item.service_type_name === 'imsi_subscription'"
        color="orange"
        variant="outlined"
        size="small"
        :to="trafficLink"
      >
        <v-icon class="mr-1">mdi-chart-timeline-variant</v-icon>
        Trafic
      </v-btn>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'ClientServiceInfo',
  props: {
    item: { type: Object, required: true },
    updating: { type: Boolean, default: false },
    showUpdateButton: { type: Boolean, default: true },
    showCommentsButton: { type: Boolean, default: true },
    commentsButtonText: { type: String, default: 'View Comments' }
  },
  emits: ['update-sim-info'],
  computed: {
    trafficLink() {
      const params = new URLSearchParams()
      if (this.item.sim_id) params.append('sim_id', this.item.sim_id)
      if (this.item.client_id) params.append('client_id', this.item.client_id)
      return `/reports/traffic?${params.toString()}`
    }
  },
  methods: {
    navigateToClient(clientId) {
      if (!clientId) return
      this.$router.push({
        name: 'clients',
        query: { client_id: clientId }
      })
    },
    navigateToSim(simId) {
      if (!simId) return
      this.$router.push({
        name: 'sims',
        query: { sim_id: simId }
      })
    },
    navigateToImsis(searchTerm) {
      if (!searchTerm) return
      this.$router.push({
        name: 'imsis',
        query: { search: searchTerm }
      })
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getSimStatusColor(status) {
      const statusLower = status?.toLowerCase()
      switch (statusLower) {
        case 'active': return 'success'
        case 'not_provisioned': return 'info'
        case 'pending': return 'warning'
        case 'suspended': return 'orange'
        case 'terminated': return 'grey'
        default: return 'grey'
      }
    }
  }
}
</script>

<style scoped>
.clickable-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
}
.clickable-link:hover {
  color: #1565c0;
}
</style>
